import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthFacade } from '@domains/identity/domain/application-services/auth.facade';

/**
 * Kolik řádků na stránku jde zvolit.
 *
 * ⚠ Musí sedět s whitelistem na serveru (`UserService.AllowedPageSizes`). Hodnota mimo
 * tenhle výčet by se v nabídce neobjevila a uživatel by ji nepřepnul zpátky.
 */
export const pageSizeChoices: number[] = [5, 10, 20, 25, 50, 100, 200];

export const defaultPageSize = 10;

/**
 * Preferovaný počet řádků v tabulkách.
 *
 * Zrcadlí {@link ThemeService}: volba se drží na **dvou místech** a každé má svůj úkol.
 * V prohlížeči proto, aby tabulka měla správnou velikost hned při prvním vykreslení —
 * než aplikace zjistí, kdo je přihlášený, už je něco na obrazovce a přeskočení z deseti
 * na padesát řádků by bylo vidět. Na účtu proto, aby volba přešla i na další zařízení.
 *
 * Po přihlášení rozhoduje účet, před ním zapamatovaná volba prohlížeče.
 */
@Injectable({ providedIn: 'root' })
export class PageSizeService {

  private static readonly storageKey = 'uiPageSize';

  private readonly sizeSubject = new BehaviorSubject<number>(this.remembered ?? defaultPageSize);

  /** Aktuální volba — tabulky se na ni mohou přihlásit a přenastavit se za běhu. */
  get size$(): Observable<number> {
    return this.sizeSubject.asObservable();
  }

  get size(): number {
    return this.sizeSubject.value;
  }

  /** Volba zapamatovaná na tomhle počítači, nebo undefined, když tu ještě nikdo nebyl. */
  private get remembered(): number | undefined {
    const stored = Number(localStorage.getItem(PageSizeService.storageKey));

    return pageSizeChoices.includes(stored) ? stored : undefined;
  }

  /**
   * Nastaví počet podle hodnoty z účtu. Nezapisuje na server — tohle je čtení toho,
   * co už tam je, ne nová volba uživatele.
   */
  applyFromAccount(pageSize: number | undefined | null): void {
    // ⚠ Když účet hodnotu nemá (starší backend, nový uživatel), **nechá se ta místní**.
    // Přepsat ji výchozí desítkou by uživateli po každém přihlášení zahodilo volbu,
    // kterou právě udělal — a vypadalo by to, že se nastavení neukládá.
    if (!pageSizeChoices.includes(Number(pageSize))) {
      return;
    }

    const size = Number(pageSize);

    localStorage.setItem(PageSizeService.storageKey, String(size));
    this.sizeSubject.next(size);
  }

  constructor(
    private injector: Injector,
  ) { }

  /**
   * Volba uživatele — zapamatuje se na tomhle počítači **a uloží na účet**.
   *
   * ⚠ `AuthFacade` se dotahuje až tady, ne konstruktorem: vede z ní řetěz zpátky na
   * `IdentityFacade`, který tuhle službu sám používá, a přímá závislost by z toho udělala
   * kruh (`NG0200`). Je to týž postup, jakým `IdentityFacade` dotahuje `ThemeService`.
   *
   * Uložení na server je „ať se to veze i na jiné zařízení" — když selže, volba na tomhle
   * počítači platí dál a uživatele to nemá čím zajímat.
   */
  use(size: number): void {
    const zvoleno = pageSizeChoices.includes(size) ? size : defaultPageSize;

    if (zvoleno === this.sizeSubject.value) {
      return;
    }

    localStorage.setItem(PageSizeService.storageKey, String(zvoleno));
    this.sizeSubject.next(zvoleno);

    this.injector.get(AuthFacade).changePageSize(zvoleno).subscribe({
      error: () => undefined,
    });
  }
}
